import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { useUploadValuerSignature } from "../../hooks/use-settings";
import { Button } from "../ui/button";

export interface Valuer {
  id: number;
  name: string;
  shortCode: string;
  qualifications: string | null;
  email: string | null;
  isActive: boolean;
  signatureUrl: string | null;
}

function SignatureUpload({
  valuerId,
  signatureUrl,
}: {
  valuerId: number | undefined;
  signatureUrl: string | null | undefined;
}): React.ReactElement {
  const uploadMutation = useUploadValuerSignature();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || valuerId === undefined) return;
    uploadMutation.mutate(
      { valuerId, file },
      {
        onSuccess: () => toast.success("Signature uploaded"),
        onError: () => toast.error("Failed to upload signature"),
      },
    );
  };

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">Signature</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={valuerId === undefined || uploadMutation.isPending}
        className="h-24 w-full rounded-md border-2 border-dashed border-input bg-muted/30 flex items-center justify-center gap-2 text-sm text-muted-foreground cursor-pointer hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="signature-upload-trigger"
      >
        {signatureUrl ? (
          <img src={signatureUrl} alt="Valuer signature" className="max-h-20 max-w-full object-contain" />
        ) : uploadMutation.isPending ? (
          <span>Uploading…</span>
        ) : valuerId === undefined ? (
          <span>Save the valuer first to upload a signature</span>
        ) : (
          <>
            <Upload className="h-4 w-4" /> Upload signature image
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        data-testid="signature-upload-input"
      />
      {uploadMutation.isError && <p className="text-xs text-destructive mt-1">Upload failed, please try again.</p>}
    </div>
  );
}

export function ValuerForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: Partial<Valuer>;
  onSave: (v: Partial<Valuer>) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [form, setForm] = useState({
    name: initial.name ?? "",
    shortCode: initial.shortCode ?? "",
    qualifications: initial.qualifications ?? "",
    email: initial.email ?? "",
    isActive: initial.isActive ?? true,
  });
  const update = (field: string, value: unknown) => setForm((f) => ({ ...f, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="v-name" className="text-xs font-medium text-muted-foreground block mb-1">
            Full Name
          </label>
          <input
            id="v-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="valuer-name"
          />
        </div>
        <div>
          <label htmlFor="v-code" className="text-xs font-medium text-muted-foreground block mb-1">
            Short Code
          </label>
          <input
            id="v-code"
            value={form.shortCode}
            onChange={(e) => update("shortCode", e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="e.g. IC"
            data-testid="valuer-short-code"
          />
        </div>
      </div>
      <div>
        <label htmlFor="v-qual" className="text-xs font-medium text-muted-foreground block mb-1">
          Qualifications
        </label>
        <input
          id="v-qual"
          value={form.qualifications}
          onChange={(e) => update("qualifications", e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="e.g. FNZIV, MRICS"
        />
      </div>
      <div>
        <label htmlFor="v-email" className="text-xs font-medium text-muted-foreground block mb-1">
          Email
        </label>
        <input
          id="v-email"
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <SignatureUpload valuerId={initial.id} signatureUrl={initial.signatureUrl} />
      <label htmlFor="v-active" className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          id="v-active"
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => update("isActive", e.target.checked)}
          className="h-4 w-4 rounded border-input"
        />
        Active
      </label>
      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave({ ...initial, ...form })} disabled={!form.name.trim()} data-testid="valuer-save">
          Save
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
